/*
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：コモンモジュール
 *
 * 作成者：2022/01/13 ノア）虞
 * 最終修正者：2022/01/13 ノア）虞
 *
 */
import { Module, VuexModule, getModule, Mutation } from 'vuex-module-decorators'
import { cloneDeep } from 'lodash'

import store from '@/store'
import { MessageListItem, NotificationsListItem, AnnounceListItem, ManagementItem } from '@/models'

/**
 * コモンモジュール
 *
 * @class
 */
@Module({
  dynamic: true,
  namespaced: true,
  name: 'favMenu',
  store
})
class CommonModule extends VuexModule {
  /**
   * 気に入りメニュー
   */
  favMenuList: string[] = []

  /**
   * 気に入り営業所
   */
  favOfficeList: string[] = []

  /**
   * マップ画面で選択された管理オブジェクト
   */
  selectedManagementObject = ''

  dynamicMqttData: ManagementItem | null = null

  notificationsMqttData: NotificationsListItem | null = null

  messageMqttData: MessageListItem | null = null

  announceMqttData: AnnounceListItem | null = null

  /**
   * 未読合計
   */
  unreadTotalCount = 0

  /**
   * 営業所選択変更（WATCH用）
   */
  businessOfficeChange = 0

  /**
   * 気に入り営業所変更（WATCH用）
   */
  favOfficeListChange = 0

  /**
   * 営業所関係エラー状態。
   */
  isBussinessOfficeStatusError = false

  /**
   * 初期化
   *
   * @returns {void}
   */
  @Mutation
  initStatus(): void {
    this.favMenuList = []
    this.favOfficeList = []
    this.selectedManagementObject = ''
    this.dynamicMqttData = null
    this.notificationsMqttData = null
    this.messageMqttData = null
    this.announceMqttData = null
    this.unreadTotalCount = 0
    this.businessOfficeChange = 0
  }

  /**
   * 営業所選択変更
   *
   * @returns {void}
   */
  @Mutation
  makeBusinessOfficeChange(): void {
    this.businessOfficeChange++
  }

  /**
   * 気に入りメニューを設定する
   *
   * @param {string[]} favMenuList 気に入り
   * @returns {void}
   */
  @Mutation
  setFavMenuList(favMenuList: string[]): void {
    this.favMenuList = cloneDeep(favMenuList)
  }

  /**
   * 気に入り営業所を設定する
   *
   * @param {string[]} favOfficeList 気に入り
   * @returns {void}
   */
  @Mutation
  setFavOfficeList(favOfficeList: string[]): void {
    this.favOfficeList = cloneDeep(favOfficeList)
    this.favOfficeListChange++
  }

  /**
   * マップ画面で選択された管理オブジェクトを設定する
   *
   * @param {string} managementObjectId 管理オブジェクトID
   * @returns {void}
   */
  @Mutation
  setSelectedManagementObject(managementObjectId: string): void {
    this.selectedManagementObject = managementObjectId
  }

  /**
   * 未読合計計算する
   *
   * @param {number[]} args 未読合計
   * @returns {void}
   */
  @Mutation
  setUnreadTotalCounts(args: number[]): void {
    let count = 0
    args.forEach(item => {
      count = count + item
    })
    this.unreadTotalCount = count
  }

  /**
   * 管理対象設定する
   *
   * @param {ManagementItem} managementItem 管理対象
   * @returns {void}
   */
  @Mutation
  setManagementItem(managementItem: ManagementItem): void {
    this.dynamicMqttData = cloneDeep(managementItem)
  }

  /**
   * 通知設定する
   *
   * @param {NotificationsListItem} notification 通知
   * @returns {void}
   */
  @Mutation
  setNotificationsData(notification: NotificationsListItem): void {
    this.notificationsMqttData = cloneDeep(notification)
  }

  /**
   * メッセージ設定する
   *
   * @param {MessageListItem} message メッセージ
   * @returns {void}
   */
  @Mutation
  setMessageData(message: MessageListItem): void {
    this.messageMqttData = cloneDeep(message)
  }

  /**
   * アナウンス設定する
   *
   * @param {AnnounceListItem} announce アナウンス
   * @returns {void}
   */
  @Mutation
  setAnnounceData(announce: AnnounceListItem): void {
    this.announceMqttData = cloneDeep(announce)
  }

  /**
   * 営業所エラー状態を設定する。
   *
   * @param {boolean} status 営業所エラー状態
   * @returns {void}
   */
  @Mutation
  setBussinessOfficeStatus(status: boolean): void {
    this.isBussinessOfficeStatusError = status
  }
}

export default getModule(CommonModule)
